import { Box, Container, Typography } from '@mui/material'
import { Link as RouterLink } from 'react-router-dom'

const TermsOfService = () => {
  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'grey.50', py: 8 }}>
      <Container maxWidth="md">
        {/* Header */}
        <Box sx={{ mb: 6 }}>
          <Typography
            component={RouterLink}
            to="/"
            sx={{
              fontSize: '1.5rem',
              fontWeight: 700,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              textDecoration: 'none',
              display: 'inline-block',
              mb: 4,
            }}
          >
            Mocksy
          </Typography>

          <Typography variant="h3" sx={{ fontWeight: 700, color: 'grey.900', mb: 2 }}>
            Terms of Service
          </Typography>
          <Typography variant="body1" sx={{ color: 'grey.600' }}>
            Last Updated: December 13, 2024
          </Typography>
        </Box>

        {/* Content */}
        <Box
          sx={{
            bgcolor: 'white',
            borderRadius: 2,
            p: 4,
            boxShadow: 1,
          }}
        >
          <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, color: 'grey.900' }}>
            1. Acceptance of Terms
          </Typography>
          <Typography variant="body1" sx={{ mb: 4, color: 'grey.700', lineHeight: 1.7 }}>
            By accessing and using Mocksy ("the Service"), you accept and agree to be bound by these
            Terms of Service ("Terms"). If you do not agree to these Terms, please do not use the
            Service.
          </Typography>

          <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, color: 'grey.900' }}>
            2. Description of Service
          </Typography>
          <Typography variant="body1" sx={{ mb: 4, color: 'grey.700', lineHeight: 1.7 }}>
            Mocksy provides an AI-powered platform for generating product image variations and
            mockups. The Service uses artificial intelligence to process uploaded images and create
            professional product photography based on user prompts.
          </Typography>

          <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, color: 'grey.900' }}>
            3. User Accounts
          </Typography>
          <Typography variant="body1" sx={{ mb: 2, color: 'grey.700', lineHeight: 1.7 }}>
            To use the Service, you must:
          </Typography>
          <Box component="ul" sx={{ mb: 4, pl: 3, color: 'grey.700', lineHeight: 1.7 }}>
            <li>Be at least 13 years of age</li>
            <li>Provide accurate and complete registration information</li>
            <li>Maintain the security of your account credentials</li>
            <li>Promptly update any outdated information</li>
            <li>Accept responsibility for all activities under your account</li>
          </Box>

          <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, color: 'grey.900' }}>
            4. Subscription and Payment
          </Typography>
          <Typography variant="body1" sx={{ mb: 2, color: 'grey.700', lineHeight: 1.7 }}>
            Subscription terms:
          </Typography>
          <Box component="ul" sx={{ mb: 4, pl: 3, color: 'grey.700', lineHeight: 1.7 }}>
            <li>Subscriptions are billed on a recurring basis</li>
            <li>Payments are processed securely through Stripe</li>
            <li>You can cancel your subscription at any time</li>
            <li>Cancellations take effect at the end of the current billing period</li>
            <li>No refunds for partial months or unused service</li>
            <li>We reserve the right to change pricing with 30 days notice</li>
          </Box>

          <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, color: 'grey.900' }}>
            5. Acceptable Use
          </Typography>
          <Typography variant="body1" sx={{ mb: 2, color: 'grey.700', lineHeight: 1.7 }}>
            You agree NOT to:
          </Typography>
          <Box component="ul" sx={{ mb: 4, pl: 3, color: 'grey.700', lineHeight: 1.7 }}>
            <li>Upload illegal, harmful, or offensive content</li>
            <li>Infringe on intellectual property rights of others</li>
            <li>Use the Service for fraudulent or deceptive purposes</li>
            <li>Attempt to reverse engineer or hack the Service</li>
            <li>Resell or redistribute generated images without proper rights</li>
            <li>Use automated tools to scrape or abuse the Service</li>
            <li>Violate any applicable laws or regulations</li>
          </Box>

          <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, color: 'grey.900' }}>
            6. Intellectual Property
          </Typography>
          <Typography variant="body1" sx={{ mb: 4, color: 'grey.700', lineHeight: 1.7 }}>
            <strong>Your Content:</strong> You retain ownership of images you upload. By using the
            Service, you grant us a limited license to process your images to provide the Service.
            <br />
            <br />
            <strong>Generated Images:</strong> You own the images generated by our AI, subject to
            these Terms. However, similar images may be generated for other users using similar
            inputs.
            <br />
            <br />
            <strong>Our Platform:</strong> The Service, including its original content, features, and
            functionality, is owned by Mocksy and protected by copyright, trademark, and other laws.
          </Typography>

          <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, color: 'grey.900' }}>
            7. Disclaimer of Warranties
          </Typography>
          <Typography variant="body1" sx={{ mb: 4, color: 'grey.700', lineHeight: 1.7 }}>
            THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND,
            EXPRESS OR IMPLIED. WE DO NOT GUARANTEE THAT THE SERVICE WILL BE UNINTERRUPTED, SECURE,
            OR ERROR-FREE. AI-GENERATED IMAGES MAY CONTAIN IMPERFECTIONS OR INACCURACIES.
          </Typography>

          <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, color: 'grey.900' }}>
            8. Limitation of Liability
          </Typography>
          <Typography variant="body1" sx={{ mb: 4, color: 'grey.700', lineHeight: 1.7 }}>
            TO THE MAXIMUM EXTENT PERMITTED BY LAW, MOCKSY SHALL NOT BE LIABLE FOR ANY INDIRECT,
            INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS OR
            REVENUES, WHETHER DIRECT OR INDIRECT, OR ANY LOSS OF DATA, USE, GOODWILL, OR OTHER
            INTANGIBLE LOSSES.
          </Typography>

          <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, color: 'grey.900' }}>
            9. Indemnification
          </Typography>
          <Typography variant="body1" sx={{ mb: 4, color: 'grey.700', lineHeight: 1.7 }}>
            You agree to indemnify and hold harmless Mocksy from any claims, damages, liabilities,
            and expenses arising from your use of the Service, violation of these Terms, or
            infringement of any rights of another party.
          </Typography>

          <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, color: 'grey.900' }}>
            10. Termination
          </Typography>
          <Typography variant="body1" sx={{ mb: 4, color: 'grey.700', lineHeight: 1.7 }}>
            We reserve the right to suspend or terminate your account at any time for violations of
            these Terms or for any other reason at our sole discretion. Upon termination, your right
            to use the Service will immediately cease.
          </Typography>

          <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, color: 'grey.900' }}>
            11. Changes to Terms
          </Typography>
          <Typography variant="body1" sx={{ mb: 4, color: 'grey.700', lineHeight: 1.7 }}>
            We reserve the right to modify these Terms at any time. We will notify users of material
            changes via email or through the Service. Continued use of the Service after changes
            constitutes acceptance of the new Terms.
          </Typography>

          <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, color: 'grey.900' }}>
            12. Governing Law
          </Typography>
          <Typography variant="body1" sx={{ mb: 4, color: 'grey.700', lineHeight: 1.7 }}>
            These Terms shall be governed by and construed in accordance with the laws of the United
            States, without regard to its conflict of law provisions.
          </Typography>

          <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, color: 'grey.900' }}>
            13. Contact Information
          </Typography>
          <Typography variant="body1" sx={{ mb: 2, color: 'grey.700', lineHeight: 1.7 }}>
            For questions about these Terms, please contact us at:
          </Typography>
          <Typography variant="body1" sx={{ color: 'grey.700', lineHeight: 1.7 }}>
            Email: legal@getmocksy.com
          </Typography>
        </Box>

        {/* Back to Home */}
        <Box sx={{ mt: 4, textAlign: 'center' }}>
          <Typography
            component={RouterLink}
            to="/"
            sx={{
              color: 'primary.main',
              textDecoration: 'none',
              '&:hover': {
                textDecoration: 'underline',
              },
            }}
          >
            ← Back to Home
          </Typography>
        </Box>
      </Container>
    </Box>
  )
}

export default TermsOfService
